import { useEffect, useState } from 'react';
import { Instance, playlist, spread } from '../../data/corpus';
import { usePlayer, usePrefs } from '../hooks';
import { PauseIcon, PlayIcon } from './Icons';
import { shortTitle } from '../copy';

/**
 * A list of real excerpts that can be heard one by one or in a row, optionally all moved to
 * C major / A minor so that only the shape is left to compare. Used wherever the atlas counts
 * something: every number stands for excerpts like these.
 */
export function Excerpts({
  items,
  chorus = 12,
  shown: initialShown = 8,
  autoplay = 0,
  note,
}: {
  items: Instance[];
  /** How many to play in a row (spread over the whole list). */
  chorus?: number;
  /** How many rows to show before "Show all". */
  shown?: number;
  /** Change this number to start the chorus playing (e.g. when the user picks a bar in a chart). */
  autoplay?: number;
  note?: string;
}) {
  const { player, position, playing } = usePlayer();
  const { prefs } = usePrefs();
  const [normalize, setNormalize] = useState(true);
  const [plan, setPlan] = useState<ReturnType<typeof playlist>['segments']>([]);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => player.setTempo(prefs.tempo), [player, prefs.tempo]);

  const run = (list: Instance[]) => {
    const p = playlist(list, normalize);
    setPlan(p.segments);
    player.setTempoMap(p.tempoMap);
    player.setNotes(p.notes, p.total);
    void player.play(0);
  };
  const sample = spread(items, chorus);
  useEffect(() => {
    if (autoplay > 0 && items.length) run(spread(items, chorus));
    // Only a new autoplay request starts playback, not a change of list.
  }, [autoplay]);
  useEffect(() => setShowAll(false), [items]);

  const current = playing ? plan.find((s) => position >= s.offset - 1e-6 && position < s.offset + s.length + 1.5) : undefined;
  if (!items.length) return <p className="muted">Nothing found by the rules yet.</p>;
  // A short list samples the whole corpus, like the chorus; "Show all" lists everything in order.
  const rows = showAll ? items : spread(items, initialShown);
  return (
    <div className="excerpts">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button className="btn" onClick={() => (playing ? player.pause() : run(sample))}>
          {playing ? <PauseIcon /> : <PlayIcon />} {playing ? 'Stop' : sample.length === 1 ? 'Hear it' : `Hear ${sample.length} in a row`}
        </button>
        <label className="field">
          <input type="checkbox" checked={normalize} onChange={(e) => setNormalize(e.target.checked)} /> All moved to C major / A minor
        </label>
      </div>
      {current && (
        <p className="muted now-playing" aria-live="polite">
          Now: <b>{shortTitle(current.inst.title, current.inst.catalogue)}</b> {current.inst.catalogue}, {current.inst.label}
        </p>
      )}
      <div className="instances">
        {rows.map((inst) => (
          <div key={`${inst.pieceId}-${inst.start}-${inst.end}`} className="instance" data-on={current?.inst === inst}>
            <button className="btn quiet" style={{ height: 28, padding: '0 8px' }} onClick={() => run([inst])} aria-label={`Play ${inst.title}, ${inst.label}`}>
              <PlayIcon />
            </button>
            <span>
              <b style={{ fontFamily: 'var(--display)', fontWeight: 500 }}>{shortTitle(inst.title, inst.catalogue)}</b>{' '}
              <span className="muted">
                {inst.catalogue} · {inst.label}
              </span>
            </span>
            <a href={`#${inst.pieceId}${inst.momentId ? `.${inst.momentId}` : ''}`}>Open</a>
          </div>
        ))}
      </div>
      {items.length > initialShown && (
        <button className="btn quiet" style={{ marginLeft: -12 }} onClick={() => setShowAll((s) => !s)}>
          {showAll ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
      {note && (
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          {note}
        </p>
      )}
    </div>
  );
}
